// Discover step — MANUAL body. The user explicitly ticks which generated conditions go into
// four buckets: long-entry, short-entry, long-exit, short-exit. Writes positional condition
// indices straight into the draft (longCondIndices / shortCondIndices / exitLongCondIndices /
// exitShortCondIndices), then backtests the assembled strategy.
//
// LAYOUT: the condition PICKER sits at a comfortable reading width; when the user hits Backtest,
// the full rich StrategyReport renders FULL WIDTH below it (same report as the Monitor tab), so
// every section is readable. Shared backtest state (useDraftBacktest) is owned by StepDiscover
// and passed in as `bt`.
//
// The condition list is fetched (debounced) from /Conditions/preview for the draft's current
// indicator set — its indices are positional into the SAME flat list /Backtests/run and the live
// evaluator rebuild, so a ticked index means exactly the same condition everywhere.

import { useEffect, useMemo, useRef } from "react";
import { Loader2, BarChart3, AlertTriangle, ChevronDown } from "lucide-react";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Checkbox } from "@/components/ui/checkbox";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip";
import { cn } from "@/lib/utils";
import { useBuilderState } from "../../../state/BuilderState";
import { useConditionPreview } from "../../../hooks/useConditionPreview";
import { biasChipClass } from "./conditionBias";
import { findContradictions } from "./conditionContradictions";
import { BacktestPane } from "./BacktestPane";
import type { DraftBacktestState } from "../../../hooks/useDraftBacktest";
import type { ConditionPreviewEntry } from "../../../api/conditions";

interface Props {
  /** Shared backtest state (owned by StepDiscover; drives the full-width results pane). */
  bt: DraftBacktestState;
  /** Advance to the Activate step (from the report's "Use this strategy"). */
  onAdvance: () => void;
}

// The four buckets, each backed by a draft field. Order = column order in the picker.
type BucketKey = "longCondIndices" | "shortCondIndices" | "exitLongCondIndices" | "exitShortCondIndices";
const BUCKETS: { key: BucketKey; label: string; short: string; tone: string }[] = [
  { key: "longCondIndices", label: "Long entry", short: "LE", tone: "text-emerald-400" },
  { key: "shortCondIndices", label: "Short entry", short: "SE", tone: "text-red-400" },
  { key: "exitLongCondIndices", label: "Long exit", short: "LX", tone: "text-emerald-300/80" },
  { key: "exitShortCondIndices", label: "Short exit", short: "SX", tone: "text-red-300/80" },
];

export function ManualConditionPicker({ bt, onAdvance }: Props) {
  const { draft, patch } = useBuilderState();
  const { conditions, loading, error } = useConditionPreview(
    draft.indicatorSpecs,
    draft.useAdvanced,
    draft.usePriceAction,
  );

  // Invalidate a stale backtest report whenever the assembled strategy changes.
  const onStrategyChanged = bt.reset;

  // Bring the full-width report into view when a backtest opens (it renders below the picker).
  const reportRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (bt.open) reportRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
  }, [bt.open]);

  // Group conditions by indicator instance so 40+ stay navigable.
  const groups = useMemo(() => {
    const m = new Map<string, ConditionPreviewEntry[]>();
    for (const c of conditions) {
      const list = m.get(c.indicatorInstanceKey) ?? [];
      list.push(c);
      m.set(c.indicatorInstanceKey, list);
    }
    return Array.from(m.entries());
  }, [conditions]);

  const hasLong = draft.longCondIndices.length > 0;
  const hasShort = draft.shortCondIndices.length > 0;
  const hasStrategy = hasLong || hasShort;

  // Contradictions only matter for entry buckets (entries are ANDed).
  const longContradictions = useMemo(
    () => findContradictions(draft.longCondIndices, conditions),
    [draft.longCondIndices, conditions],
  );
  const shortContradictions = useMemo(
    () => findContradictions(draft.shortCondIndices, conditions),
    [draft.shortCondIndices, conditions],
  );

  const isTicked = (bucket: BucketKey, index: number) => draft[bucket].includes(index);

  const toggle = (bucket: BucketKey, index: number) => {
    const current = draft[bucket];
    const next = current.includes(index)
      ? current.filter((i) => i !== index)
      : [...current, index].sort((a, b) => a - b);
    patch({ [bucket]: next });
    onStrategyChanged();
  };

  const bucketCount = (bucket: BucketKey) => draft[bucket].length;

  return (
    <div className="flex flex-col gap-5">
      <div>
        <h2 className="text-lg font-bold">Build conditions by hand</h2>
        <p className="text-xs text-muted-foreground mt-0.5 max-w-2xl">
          Tick which conditions trigger each side. <span className="text-emerald-400 font-semibold">Entries</span> are
          ANDed (all ticked must fire together to open). <span className="font-semibold text-foreground">Exits</span> close
          that side when they fire. Then <span className="font-semibold text-foreground">Backtest</span> the combined
          long + short strategy.
        </p>
      </div>

      {/* Picker — kept at a comfortable reading width; the report below spans full width. */}
      <div className="flex flex-col gap-4 max-w-4xl">
        {/* Assembled summary + Backtest */}
        <Card className="p-3 bg-card flex items-center gap-4 flex-wrap">
          <span className="text-[10px] uppercase tracking-widest text-muted-foreground/60">Assembled</span>
          {BUCKETS.map((b) => (
            <span key={b.key} className="inline-flex items-center gap-1.5 text-[11px]">
              <span className={cn("font-semibold", b.tone)}>{b.label}</span>
              <span className={cn("font-mono", bucketCount(b.key) > 0 ? "text-foreground" : "text-muted-foreground/50")}>
                {bucketCount(b.key)}
              </span>
            </span>
          ))}
          <Button
            size="sm"
            onClick={() => void bt.run(draft)}
            disabled={!hasStrategy}
            className="ml-auto bg-primary hover:bg-primary/90 text-primary-foreground"
            title={hasStrategy ? "Backtest the assembled strategy" : "Tick at least one long-entry or short-entry condition"}
          >
            <BarChart3 className="size-3.5" />
            Backtest
          </Button>
        </Card>

        {!hasStrategy && (
          <p className="text-xs text-muted-foreground italic">
            Tick at least one <span className="text-emerald-400">long-entry</span> or{" "}
            <span className="text-red-400">short-entry</span> condition — a strategy needs an entry trigger to open a position.
          </p>
        )}

        {/* Contradiction warnings (entries only) */}
        {[
          { label: "Long entry", items: longContradictions },
          { label: "Short entry", items: shortContradictions },
        ].map(({ label, items }) =>
          items.length === 0 ? null : (
            <div key={label} className="flex items-start gap-2 rounded-xl border border-amber-500/40 bg-amber-500/5 px-3 py-2 text-[11px] text-amber-300">
              <AlertTriangle className="size-3.5 mt-0.5 shrink-0" />
              <div>
                <span className="font-semibold">{label} can never fire.</span>{" "}
                {items.map((c) => `${c.names.join(" ∧ ")} (${c.indicatorInstanceKey})`).join("; ")} — these are ANDed but
                can't be true on the same bar. The backtest will show 0 entries on this side.
              </div>
            </div>
          ),
        )}

        {error && (
          <Card className="p-4 bg-card-elevated text-sm text-destructive">Couldn't load conditions: {error}</Card>
        )}

        {loading && conditions.length === 0 && (
          <Card className="p-10 text-center bg-card-elevated">
            <Loader2 className="size-6 mx-auto mb-3 text-primary animate-spin" />
            <p className="text-sm text-muted-foreground">Generating conditions from your indicators…</p>
          </Card>
        )}

        {!loading && conditions.length === 0 && !error && (
          <Card className="p-8 text-center bg-card-elevated text-sm text-muted-foreground">
            No conditions yet. Add at least one indicator in the Setup step.
          </Card>
        )}

        {/* The picker: one collapsible section per indicator instance, four checkbox columns per row.
            While a re-fetch is in flight (loading && conditions already shown), disable interaction —
            the visible rows are the STALE list, so a tick now could land on a condition the user
            didn't see once the new list resolves. */}
        {conditions.length > 0 && (
          <Card className={cn("p-0 bg-card overflow-hidden", loading && "pointer-events-none opacity-60")} aria-busy={loading}>
            {/* Sticky column header */}
            <div className="flex items-center gap-2 px-3 py-2 border-b border-border bg-card-elevated/60 text-[10px] uppercase tracking-wider text-muted-foreground/70 sticky top-0 z-10">
              <span className="flex-1">Condition</span>
              {BUCKETS.map((b) => (
                <span key={b.key} className={cn("w-12 text-center font-semibold", b.tone)} title={b.label}>
                  {b.short}
                </span>
              ))}
            </div>

            <div className="max-h-[60vh] overflow-y-auto">
              {groups.map(([instanceKey, items]) => {
                const tickedInGroup = items.reduce(
                  (n, c) => n + BUCKETS.reduce((m, b) => m + (isTicked(b.key, c.index) ? 1 : 0), 0),
                  0,
                );
                return (
                  <Collapsible key={instanceKey} defaultOpen>
                    <CollapsibleTrigger className="group flex items-center gap-2 w-full px-3 py-2 border-b border-border/60 hover:bg-card-elevated/40 transition-colors">
                      <ChevronDown className="size-3.5 text-muted-foreground transition-transform group-data-[state=closed]:-rotate-90" />
                      <span className="text-xs font-semibold font-mono">{instanceKey}</span>
                      <span className="text-[10px] text-muted-foreground">{items.length} conditions</span>
                      {tickedInGroup > 0 && (
                        <Badge variant="outline" className="ml-auto border-primary/40 text-primary text-[10px]">
                          {tickedInGroup} ticked
                        </Badge>
                      )}
                    </CollapsibleTrigger>
                    <CollapsibleContent>
                      {items.map((c) => (
                        <div
                          key={c.id}
                          className="flex items-center gap-2 px-3 py-1.5 border-b border-border/30 hover:bg-card-elevated/30"
                        >
                          <div className="flex-1 min-w-0 flex items-center gap-2">
                            <Badge variant="outline" className={cn("font-mono text-[10px] shrink-0", biasChipClass(c.bias))}>
                              {c.name}
                            </Badge>
                            {c.description && (
                              <Tooltip delayDuration={150}>
                                <TooltipTrigger asChild>
                                  <span className="text-[10px] text-muted-foreground/70 truncate cursor-help">{c.description}</span>
                                </TooltipTrigger>
                                <TooltipContent side="top" className="max-w-[260px] text-xs leading-snug">
                                  {c.description}
                                </TooltipContent>
                              </Tooltip>
                            )}
                          </div>
                          {BUCKETS.map((b) => (
                            <div key={b.key} className="w-12 flex justify-center">
                              <Checkbox
                                checked={isTicked(b.key, c.index)}
                                onCheckedChange={() => toggle(b.key, c.index)}
                                aria-label={`${c.name} — ${b.label}`}
                              />
                            </div>
                          ))}
                        </div>
                      ))}
                    </CollapsibleContent>
                  </Collapsible>
                );
              })}
            </div>
          </Card>
        )}
      </div>

      {/* Full-width backtest report — same rich StrategyReport as the Monitor tab. */}
      {bt.open && (
        <div ref={reportRef} className="scroll-mt-4">
          <BacktestPane
            result={bt.result}
            running={bt.running}
            hasStrategy={hasStrategy}
            coin={draft.coin}
            interval={draft.interval}
            marketType={draft.marketType}
            onClose={bt.close}
            onAdvance={onAdvance}
          />
        </div>
      )}
    </div>
  );
}
